import supabase from "./supabase";
import { useQuery } from "react-query";

/**
 * Calculate payout data for a division
 * @param {Object} params - Parameters for payout calculation
 * @param {string} params.eventId - Event ID
 * @param {Object} params.division - Division data
 * @param {Object} params.divisionSettings - Division settings (payoutPercentage, selectedPaytableId)
 * @param {boolean} params.scoringOrderGolf - Whether golf scoring is used
 * @returns {Promise<Object>} Payout calculation results
 */
export async function calculateDivisionPayouts({
  eventId,
  division,
  divisionSettings,
  scoringOrderGolf,
}) {
  // Get the exact same data that DivisionPayoutTab is using - single query
  const response = await supabase
    .from("event_entries")
    .select(
      `
      id,
      user_id,
      users_public(name),
      score,
      score_adv,
      is_dnf,
      is_dropped,
      event_divisions_id,
      payouts(payout_amount)
    `
    )
    .eq("event_id", eventId)
    .eq("event_divisions_id", division.event_divisions_id);

  if (response.error) {
    throw new Error(`Error fetching division data: ${response.error.message}`);
  }

  const divisionPlayers = response.data || [];

  if (!divisionPlayers || divisionPlayers.length === 0) {
    return {
      players: [],
      playersWithRanks: [],
      totalPlayers: 0,
      placesPaid: 0,
      prizePool: 0,
      allPayouts: {},
      payoutData: [],
    };
  }

  // Filter and sort exactly like DivisionPayoutTab does
  const divisionActivePlayers = divisionPlayers.filter(
    (player) => !player.is_dnf
  );

  if (divisionActivePlayers.length === 0) {
    return {
      players: divisionPlayers,
      playersWithRanks: [],
      totalPlayers: 0,
      placesPaid: 0,
      prizePool: 0,
      allPayouts: {},
      payoutData: [],
    };
  }

  const totalPlayers = divisionActivePlayers.length;
  const placesPaid = Math.ceil(
    (totalPlayers * divisionSettings.payoutPercentage) / 100
  );

  // Sort players exactly like DivisionPayoutTab
  divisionActivePlayers.sort((a, b) => {
    if (scoringOrderGolf) {
      // Golf scoring: lower scores are better
      return a.score - b.score;
    } else {
      // Point scoring: higher scores are better
      return b.score - a.score;
    }
  });

  // Calculate ranks exactly like DivisionPayoutTab does
  const playersWithRanks = divisionActivePlayers.map((player) => {
    // Count how many players have strictly better scores
    let betterPlayersCount = 0;
    for (let i = 0; i < divisionActivePlayers.length; i++) {
      const otherPlayer = divisionActivePlayers[i];
      if (scoringOrderGolf) {
        // Golf scoring: lower scores are better
        if (otherPlayer.score < player.score) {
          betterPlayersCount++;
        }
      } else {
        // Point scoring: higher scores are better
        if (otherPlayer.score > player.score) {
          betterPlayersCount++;
        }
      }
    }
    const rank = betterPlayersCount + 1;
    return { ...player, rank };
  });

  // Calculate prize pool exactly like DivisionPayoutTab
  const prizePool =
    ((division.division_cost || 0) - (division.division_rake_per_player || 0)) *
    totalPlayers;

  // Get paytable data exactly like DivisionPayoutTab
  let paytableData = [];
  if (divisionSettings.selectedPaytableId) {
    const payoutDataResponse = await supabase
      .from("paytables_data")
      .select("rank, percentage")
      .eq("paytable_id", divisionSettings.selectedPaytableId)
      .eq("places_paid", placesPaid)
      .order("rank");

    if (!payoutDataResponse.error) {
      paytableData = payoutDataResponse.data || [];
    } else {
      console.error("Error fetching paytable data:", payoutDataResponse.error);
    }
  }

  // Calculate payouts exactly like DivisionPayoutTab does
  const calculateAllPayouts = () => {
    const payouts = {};

    for (let rank = 1; rank <= placesPaid; rank++) {
      if (!paytableData || paytableData.length === 0) {
        const fallbackPercentage = 1 / placesPaid;
        const amount = prizePool * fallbackPercentage;

        payouts[rank] = {
          baseAmount: amount,
          roundedAmount: Math.round(amount * 100) / 100,
          percentage: fallbackPercentage,
        };
      } else {
        const payoutEntry = paytableData.find((entry) => entry.rank === rank);
        if (payoutEntry) {
          const amount = prizePool * payoutEntry.percentage;

          payouts[rank] = {
            baseAmount: amount,
            roundedAmount: Math.round(amount * 100) / 100,
            percentage: payoutEntry.percentage,
          };
        }
      }
    }

    return payouts;
  };

  const allPayouts = calculateAllPayouts();

  // Get payout info exactly like DivisionPayoutTab does
  const getPayoutInfo = (rank) => {
    if (typeof rank !== "number" || rank > placesPaid) {
      return { amount: 0, percentage: 0, status: "No" };
    }

    const tiedPlayers = playersWithRanks.filter(
      (player) => player.rank === rank
    );
    const tieCount = tiedPlayers.length;

    if (tieCount === 0) {
      return { amount: 0, percentage: 0, status: "No" };
    }

    let combinedPayout = 0;
    let combinedPercentage = 0;

    for (let r = rank; r < rank + tieCount && r <= placesPaid; r++) {
      const rankPayoutInfo = allPayouts[r];
      if (rankPayoutInfo) {
        combinedPayout += rankPayoutInfo.roundedAmount;
        combinedPercentage += rankPayoutInfo.percentage || 1 / placesPaid;
      }
    }

    const splitAmount = combinedPayout / tieCount;
    const splitPercentage = combinedPercentage / tieCount;

    return {
      amount: Math.round(splitAmount * 100) / 100,
      percentage: splitPercentage,
      status: "Yes",
    };
  };

  // Generate payout data for database insertion
  const payoutData = [];
  playersWithRanks.forEach((player) => {
    const payoutInfo = getPayoutInfo(player.rank);
    if (payoutInfo.status === "Yes") {
      payoutData.push({
        event_entries_id: player.id,
        payout: payoutInfo.amount,
        rank: player.rank,
        division_name: division.divisions.name,
        player_name: player.users_public?.name || "Unknown",
        score: player.score,
        percentage: payoutInfo.percentage,
      });
    }
  });

  // Ensure total payouts equal prize pool exactly
  if (payoutData.length > 0) {
    const totalCalculatedPayouts = payoutData.reduce(
      (sum, payout) => sum + payout.payout,
      0
    );
    const difference = prizePool - totalCalculatedPayouts;

    // If there's any difference (even tiny), adjust the largest payout to match the prize pool
    if (Math.abs(difference) > 0.000001) {
      // Very small threshold to catch any floating point differences
      // Find the largest payout to adjust
      const largestPayoutIndex = payoutData.reduce(
        (maxIndex, payout, index) => {
          return payout.payout > payoutData[maxIndex].payout ? index : maxIndex;
        },
        0
      );

      // Adjust the largest payout and round to exactly 2 decimal places
      const adjustedAmount = payoutData[largestPayoutIndex].payout + difference;
      payoutData[largestPayoutIndex].payout =
        Math.round(adjustedAmount * 100) / 100;

      // Verify the total now matches exactly
      const verifyTotal = payoutData.reduce(
        (sum, payout) => sum + payout.payout,
        0
      );
      const finalDifference = Math.abs(prizePool - verifyTotal);

      if (finalDifference > 0.01) {
        console.warn(
          `⚠️ Still ${finalDifference.toFixed(3)} difference after adjustment`
        );
      }
    }
  }

  return {
    players: divisionPlayers,
    playersWithRanks,
    totalPlayers,
    placesPaid,
    prizePool,
    allPayouts,
    paytableData,
    getPayoutInfo,
    payoutData,
  };
}

/**
 * Calculate payout summary and validation
 * @param {Object} allPayouts - All payout data by rank
 * @param {Array} paytableData - Paytable data
 * @param {number} prizePool - Total prize pool
 * @param {number} placesPaid - Number of places paid
 * @returns {Object} Payout summary with validation
 */
export function calculatePayoutSummary(
  allPayouts,
  paytableData,
  prizePool,
  placesPaid
) {
  const totalAmount = Object.values(allPayouts).reduce(
    (sum, payout) => sum + payout.roundedAmount,
    0
  );

  if (!paytableData || paytableData.length === 0) {
    // Fallback case - equal distribution should always sum to 100%
    const totalPercentage = 1.0; // 100%
    return {
      totalPercentage,
      totalAmount,
      isValid: Math.abs(prizePool - totalAmount) < 0.001,
      difference: Math.abs(prizePool - totalAmount),
    };
  }

  // Sum up all payout percentages
  const totalPercentage = paytableData.reduce(
    (sum, entry) => sum + entry.percentage,
    0
  );
  const difference = Math.abs(prizePool - totalAmount);
  const isValid = Math.abs(totalPercentage - 1.0) < 0.001 && difference < 0.001;

  return {
    totalPercentage,
    totalAmount,
    isValid,
    difference,
  };
}

/**
 * React hook for division payout calculations
 * @param {string} eventId - Event ID
 * @param {Object} divisionData - Division data object
 * @param {Object} divisionSettings - Division settings (payoutPercentage, selectedPaytableId)
 * @param {boolean} scoringOrderGolf - Whether golf scoring is used
 * @returns {Object} React Query result with payout data
 */
export function useDivisionPayoutCalculations(
  eventId,
  divisionData,
  divisionSettings,
  scoringOrderGolf
) {
  return useQuery(
    [
      "divisionPayoutCalculations",
      eventId,
      divisionData?.event_divisions_id,
      divisionSettings?.payoutPercentage,
      divisionSettings?.selectedPaytableId,
      scoringOrderGolf,
    ],
    async () => {
      if (!eventId || !divisionData || !divisionSettings) {
        throw new Error("Missing required parameters for payout calculation");
      }

      return await calculateDivisionPayouts({
        eventId,
        division: divisionData,
        divisionSettings,
        scoringOrderGolf,
      });
    },
    {
      enabled: !!(eventId && divisionData && divisionSettings),
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
}
